import * as THREE from 'three';
import EventEmitter from 'eventemitter3';
import { HandGestureTracker, SwingGuard, type Handedness } from '@/xr/handInput';
import { rayPanelUv, type PointerRay } from '@/xr/panelRay';
import { colors } from '@/utils/colors';

export type WristMenuButton = {
  id: string;
  /** Called on every redraw — labels can reflect live state ("Units: YD"). */
  label: () => string;
};

export type WristMenuStatus = { text: string; tone: 'ok' | 'warn' | 'bad' };

export type WristMenuOptions = {
  /** Current club/preset name for the selector row. */
  clubLabel: () => string;
  /** Buttons for the grid, laid out two per row (odd last one spans). */
  buttons: WristMenuButton[];
  /** Panel width in meters. */
  widthMeters?: number;
  /**
   * Optional status chip drawn above the club row. Non-interactive — no poke
   * region is registered for it.
   */
  status?: () => WristMenuStatus;
  /**
   * Height above the palm plane, in metres. Smaller sits closer to the hand but
   * further from the eye, since the offset points back toward the face.
   */
  palmOffset?: number;
  /**
   * Distance along the hand toward the fingertips, in metres. The wrist joint is
   * at the heel of the hand, so without this the panel hangs over the forearm.
   */
  distalOffset?: number;
};

interface EventMap {
  openChanged: (open: boolean) => void;
  /** A button (or 'club-prev'/'club-next') was poked. */
  select: (id: string) => void;
}

// Open/close pose thresholds — tune on device
const PALM_FACING_DOT = 0.55;
const GAZE_ANGLE_COS = Math.cos(THREE.MathUtils.degToRad(22));
const WRIST_MIN_DIST = 0.15;
const WRIST_MAX_DIST = 0.65;
const MAX_WRIST_SPEED = 0.5;
const OPEN_HOLD_S = 0.25;
const CLOSE_HOLD_S = 0.5;

// Poke thresholds
const HOVER_RANGE = 0.06;
const PRESS_DEPTH = 0.012;
const REPRESS_S = 0.25;

/** Ray-pointer mode: parked distance in front of the head, and its idle timeout. */
const RAY_DISTANCE = 0.55;
const RAY_IDLE_CLOSE_S = 12;
/** How long a ray-triggered button stays lit. Pokes light while held instead. */
const PRESS_FLASH_S = 0.18;

const CANVAS_W = 512;
const ROW_H = 96;
const CLUB_ROW_H = 120;
const CHIP_ROW_H = 52;
const PAD = 20;

const CHIP_TONES: Record<WristMenuStatus['tone'], string> = {
  ok: colors.primary ?? '#21d48d',
  warn: colors.yellow ?? 'rgb(255, 202, 68)',
  bad: colors.red ?? '#ee3333',
};

type Region = { id: string; x: number; y: number; w: number; h: number };

/**
 * The Quest hand-menu pattern: raise a wrist with the palm toward your face
 * while gazing at it → this panel fades in above the wrist; poke buttons with
 * the other hand's index fingertip. The pose is physically incompatible with
 * a club grip (and gated by the SwingGuard besides), so it can never trigger
 * mid-swing. Poking is positional — it deliberately bypasses the pinch/guard
 * gesture machinery.
 */
export class WristMenu extends EventEmitter<EventMap> {
  object: THREE.Mesh;
  isOpen = false;
  /** The hand wearing the menu (the OTHER hand pokes). */
  activeHand?: Handedness;

  #clubLabel: () => string;
  #buttons: WristMenuButton[];
  #status?: () => WristMenuStatus;
  #chipH: number;
  #palmOffset: number;
  #distalOffset: number;
  #regions: Region[] = [];
  #canvasH: number;
  #width: number;
  #height: number;

  #canvas: HTMLCanvasElement;
  #ctx: CanvasRenderingContext2D;
  #texture: THREE.CanvasTexture;
  #cacheKey = '';

  #openTimer = 0;
  #closeTimer = 0;
  #candidateHand?: Handedness;
  #hover?: string;
  #pressed?: string;
  #prevPokeDepth = Infinity;
  #lastPressAt = -Infinity;
  #elapsed = 0;
  #hasPose = false;
  #forcedOpen = false;
  /** Parked in front of the player and driven by a controller ray, not a wrist. */
  #rayOpen = false;
  #lastRayHitAt = 0;
  #pressFlashUntil = 0;

  // scratch
  #toHead = new THREE.Vector3();
  #headFwd = new THREE.Vector3();
  #desired = new THREE.Vector3();
  #local = new THREE.Vector3();

  constructor(options: WristMenuOptions) {
    super();
    this.#clubLabel = options.clubLabel;
    this.#buttons = options.buttons;
    this.#status = options.status;
    this.#chipH = options.status ? CHIP_ROW_H : 0;
    this.#palmOffset = options.palmOffset ?? 0.06;
    this.#distalOffset = options.distalOffset ?? 0.045;
    this.#width = options.widthMeters ?? 0.21;

    // Layout: optional status chip + club selector row + 2-column grid (odd
    // last button spans). Height must be final before #buildRegions, which
    // normalizes every region against it.
    const rows = Math.ceil(this.#buttons.length / 2);
    this.#canvasH = PAD + this.#chipH + CLUB_ROW_H + rows * (ROW_H + PAD / 2) + PAD;
    this.#height = this.#width * (this.#canvasH / CANVAS_W);
    this.#buildRegions();

    this.#canvas = document.createElement('canvas');
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = this.#canvasH;
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get 2d context for wrist menu');
    this.#ctx = ctx;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;

    this.object = new THREE.Mesh(
      new THREE.PlaneGeometry(this.#width, this.#height),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false,
        fog: false,
        side: THREE.DoubleSide,
      })
    );
    this.object.name = 'WristMenu';
    this.object.renderOrder = 40; // over everything, including the hint
    this.object.userData.mrMaskExempt = true;
    this.object.visible = false;

    this.#draw();
  }

  update(
    dt: number,
    hands: { left: HandGestureTracker; right: HandGestureTracker },
    guard: SwingGuard,
    head: { position: THREE.Vector3; quaternion: THREE.Quaternion },
  ) {
    this.#elapsed += dt;
    if (this.#forcedOpen) return; // desktop layout preview owns the pose
    if (this.#rayOpen) return;    // a controller ray owns the panel — see updateRay

    if (!this.isOpen) {
      // Either hand can wear the menu — first to hold the pose wins
      const qualifying = (['left', 'right'] as const).find(handedness =>
        this.#qualifies(hands[handedness], guard, head));
      if (qualifying && qualifying === this.#candidateHand) {
        this.#openTimer += dt;
        if (this.#openTimer >= OPEN_HOLD_S) this.#setOpen(true, qualifying);
      } else {
        this.#candidateHand = qualifying;
        this.#openTimer = 0;
      }
      return;
    }

    // Open: track the wrist, close after sustained pose loss
    const wearer = hands[this.activeHand!];
    if (this.#qualifies(wearer, guard, head, true)) {
      this.#closeTimer = 0;
    } else {
      this.#closeTimer += dt;
      if (this.#closeTimer >= CLOSE_HOLD_S) {
        this.#setOpen(false);
        return;
      }
    }

    this.#updatePose(wearer, head, dt);
    const poker = hands[this.activeHand === 'left' ? 'right' : 'left'];
    this.#updatePoke(poker);
    // Otherwise a status change while the menu is open and idle would never
    // repaint — #draw is only reached on open and on hover/press changes. The
    // cache key makes this a no-op unless the text actually differs.
    this.#draw();
  }

  /**
   * Open the panel parked in front of the player, to be driven by a controller
   * ray instead of a fingertip. Swing mode needs this: both hands are on the
   * club, so there is neither a wrist to wear the panel nor a finger to poke it.
   *
   * Parked rather than head-following, for the same reason InfoPanel parks — a
   * target that eases every frame is impossible to rest a laser on.
   */
  openForRay(head: { position: THREE.Vector3; quaternion: THREE.Quaternion }, distance = RAY_DISTANCE) {
    this.#headFwd.set(0, 0, -1).applyQuaternion(head.quaternion);
    this.object.position.copy(head.position).addScaledVector(this.#headFwd, distance);
    // Yaw-only facing keeps the labels upright whatever the head is doing
    this.object.rotation.set(0, Math.atan2(this.#headFwd.x, this.#headFwd.z) + Math.PI, 0);
    this.#rayOpen = true;
    this.#lastRayHitAt = this.#elapsed;
    this.#hasPose = true;
    this.#setOpen(true);
  }

  /** Whether the panel is currently the ray-driven, parked variant. */
  get isRayOpen(): boolean {
    return this.#rayOpen;
  }

  /**
   * Hover handling for controller rays. Returns the ray that landed on the panel
   * so the caller can rest its pointer dot on the surface. Call every frame
   * while `isRayOpen`; `activateHover` is what actually clicks.
   */
  updateRay(dt: number, rays: readonly PointerRay[]): { rayIndex: number; distance: number; point: THREE.Vector3 } | undefined {
    this.#elapsed += dt;
    if (!this.#rayOpen || !this.isOpen) return undefined;

    let best: { rayIndex: number; distance: number; point: THREE.Vector3 } | undefined;
    let hover: string | undefined;
    for (let i = 0; i < rays.length; i++) {
      const hit = rayPanelUv(rays[i], this.object, this.#width, this.#height);
      if (!hit || (best && hit.distance >= best.distance)) continue;
      best = { rayIndex: i, distance: hit.distance, point: hit.point };
      hover = this.#regionAt(hit.u, hit.v)?.id;
    }

    if (best) this.#lastRayHitAt = this.#elapsed;
    else if (this.#elapsed - this.#lastRayHitAt > RAY_IDLE_CLOSE_S) {
      // Forgotten, floating in the room — don't leave it there
      this.close();
      return undefined;
    }

    const pressed = this.#elapsed < this.#pressFlashUntil ? this.#pressed : undefined;
    if (hover !== this.#hover || pressed !== this.#pressed) {
      this.#hover = hover;
      this.#pressed = pressed;
    }
    this.#draw(); // cache key makes this a no-op unless something changed
    return best;
  }

  /** Click whatever the ray is hovering (wired to the controller trigger). */
  activateHover(): boolean {
    if (!this.isOpen || !this.#hover) return false;
    if (this.#elapsed - this.#lastPressAt <= REPRESS_S) return false;
    this.#lastPressAt = this.#elapsed;
    this.#pressed = this.#hover;
    this.#pressFlashUntil = this.#elapsed + PRESS_FLASH_S;
    this.#draw();
    this.emit('select', this.#hover);
    return true;
  }

  /** Close from outside (mode change, session end, a menu action that dismisses). */
  close() {
    this.#rayOpen = false;
    this.#setOpen(false);
  }

  /** Desktop `?sim=1` layout preview — parks the panel in front of a camera. */
  forceOpenAt(camera: THREE.Camera) {
    this.#forcedOpen = !this.#forcedOpen;
    if (this.#forcedOpen) {
      this.isOpen = true;
      this.object.visible = true;
      this.#headFwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
      this.object.position.copy(camera.position).addScaledVector(this.#headFwd, 0.5);
      this.object.quaternion.copy(camera.quaternion);
      this.#draw();
      this.emit('openChanged', true);
    } else {
      this.#setOpen(false);
    }
  }

  /** Session end: drop all transient state. */
  reset() {
    this.#forcedOpen = false;
    this.#rayOpen = false;
    this.#setOpen(false);
    this.#openTimer = 0;
    this.#candidateHand = undefined;
  }

  #qualifies(
    tracker: HandGestureTracker,
    guard: SwingGuard,
    head: { position: THREE.Vector3; quaternion: THREE.Quaternion },
    lenient = false,
  ): boolean {
    if (!tracker.tracked || guard.isSuppressed(tracker.handedness)) return false;
    if (!lenient && tracker.speed > MAX_WRIST_SPEED) return false;

    this.#toHead.copy(head.position).sub(tracker.wrist);
    const dist = this.#toHead.length();
    if (dist < WRIST_MIN_DIST || dist > WRIST_MAX_DIST * (lenient ? 1.2 : 1)) return false;
    this.#toHead.divideScalar(dist);

    // Palm faces the face
    const facing = tracker.palmNormal.dot(this.#toHead);
    if (facing < PALM_FACING_DOT * (lenient ? 0.7 : 1)) return false;

    // Gaze at the wrist
    this.#headFwd.set(0, 0, -1).applyQuaternion(head.quaternion);
    const gaze = this.#headFwd.dot(this.#toHead.negate());
    return gaze > (lenient ? GAZE_ANGLE_COS * 0.85 : GAZE_ANGLE_COS);
  }

  #setOpen(open: boolean, hand?: Handedness) {
    if (open === this.isOpen && (!open || hand === this.activeHand)) return;
    this.isOpen = open;
    this.activeHand = open ? hand : undefined;
    this.object.visible = open;
    this.#openTimer = 0;
    this.#closeTimer = 0;
    this.#hover = undefined;
    this.#pressed = undefined;
    this.#pressFlashUntil = 0;
    this.#prevPokeDepth = Infinity;
    // The ray variant places its own pose before opening and must keep it
    if (!this.#rayOpen) this.#hasPose = false;
    if (open) this.#draw();
    this.emit('openChanged', open);
  }

  #updatePose(tracker: HandGestureTracker, head: { position: THREE.Vector3 }, dt: number) {
    // Sit on the hand, not out in front of it. The wrist joint is at the heel
    // of the hand, so the distal term is what moves the panel off the forearm
    // and onto the palm; the palm term is the only gap left, and it points back
    // toward the face — which is why shrinking it also costs apparent size.
    this.#desired.copy(tracker.wrist)
      .addScaledVector(tracker.handForward, this.#distalOffset)
      .addScaledVector(tracker.palmNormal, this.#palmOffset);

    if (!this.#hasPose) {
      this.object.position.copy(this.#desired);
      this.#hasPose = true;
    } else {
      this.object.position.lerp(this.#desired, 1 - Math.exp(-10 * dt));
    }
    this.object.lookAt(head.position);
  }

  #updatePoke(poker: HandGestureTracker) {
    let hover: string | undefined;
    let depth = Infinity;

    if (poker.tracked) {
      // Panel local space: x∈[-w/2,w/2], y∈[-h/2,h/2], +z toward the viewer
      this.#local.copy(poker.indexTip);
      this.object.worldToLocal(this.#local);
      depth = this.#local.z;
      if (depth > -PRESS_DEPTH && depth < HOVER_RANGE) {
        const u = this.#local.x / this.#width + 0.5;
        const v = 0.5 - this.#local.y / this.#height;
        hover = this.#regionAt(u, v)?.id;
      }
    }

    // Press on crossing the surface while over a region
    let pressed: string | undefined;
    if (
      hover &&
      depth <= PRESS_DEPTH &&
      this.#prevPokeDepth > PRESS_DEPTH &&
      this.#elapsed - this.#lastPressAt > REPRESS_S
    ) {
      pressed = hover;
      this.#lastPressAt = this.#elapsed;
      this.emit('select', hover);
    }
    // On tracking loss, require a fresh surface crossing before the next press.
    // Infinity would satisfy `#prevPokeDepth > PRESS_DEPTH`, so a hand that
    // re-acquires already inside a region would fire instantly — likelier now
    // that the panel sits close to the wrist and the hands occlude each other.
    this.#prevPokeDepth = poker.tracked ? depth : NaN;

    if (hover !== this.#hover || pressed !== this.#pressed) {
      this.#hover = hover;
      this.#pressed = pressed;
      this.#draw();
    }
  }

  #buildRegions() {
    // Normalized (0..1) canvas-space regions. No region is registered for the
    // status chip: #regionAt is a whitelist, so unregistered pixels can never
    // be poked.
    const clubY = (PAD + this.#chipH) / this.#canvasH;
    const clubH = CLUB_ROW_H / this.#canvasH;
    const arrowW = 0.22;
    this.#regions.push(
      { id: 'club-prev', x: 0.03, y: clubY, w: arrowW, h: clubH },
      { id: 'club-next', x: 0.97 - arrowW, y: clubY, w: arrowW, h: clubH },
    );

    let y = PAD + this.#chipH + CLUB_ROW_H + PAD / 2;
    for (let i = 0; i < this.#buttons.length; i += 2) {
      const pair = this.#buttons.slice(i, i + 2);
      const cellW = pair.length === 2 ? 0.455 : 0.94;
      pair.forEach((button, column) => {
        this.#regions.push({
          id: button.id,
          x: 0.03 + column * 0.485,
          y: y / this.#canvasH,
          w: cellW,
          h: ROW_H / this.#canvasH,
        });
      });
      y += ROW_H + PAD / 2;
    }
  }

  #regionAt(u: number, v: number): Region | undefined {
    return this.#regions.find(r => u >= r.x && u <= r.x + r.w && v >= r.y && v <= r.y + r.h);
  }

  #draw() {
    const status = this.#status?.();
    // The chip must be in the key or a connection change would never repaint
    const key = `${this.#hover}|${this.#pressed}|${this.#clubLabel()}`
      + `|${status?.text ?? ''}|${status?.tone ?? ''}`
      + `|${this.#buttons.map(b => b.label()).join(',')}`;
    if (key === this.#cacheKey && this.#texture.image) return;
    this.#cacheKey = key;

    const ctx = this.#ctx;
    const H = this.#canvasH;
    ctx.clearRect(0, 0, CANVAS_W, H);

    ctx.fillStyle = 'rgba(17, 28, 28, 0.92)';
    ctx.beginPath();
    ctx.roundRect(0, 0, CANVAS_W, H, 26);
    ctx.fill();
    ctx.strokeStyle = 'rgba(33, 212, 141, 0.4)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const drawRegion = (region: Region, label: string, big = false) => {
      const x = region.x * CANVAS_W;
      const y = region.y * H;
      const w = region.w * CANVAS_W;
      const h = region.h * H;
      if (this.#pressed === region.id) {
        ctx.fillStyle = colors.primary;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 16);
        ctx.fill();
      } else if (this.#hover === region.id) {
        ctx.strokeStyle = colors.primary;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 16);
        ctx.stroke();
      } else {
        ctx.strokeStyle = 'rgba(255,255,255,0.22)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 16);
        ctx.stroke();
      }
      ctx.font = `bold ${big ? 56 : 34}px Rubik, sans-serif`;
      ctx.fillStyle = this.#pressed === region.id ? '#111c1c' : '#ffffff';
      ctx.fillText(label, x + w / 2, y + h / 2, w - 16);
    };

    // Status chip: a pill above the club row, tinted by tone. Not pokeable.
    if (status) {
      const tone = CHIP_TONES[status.tone];
      const chipY = PAD + (CHIP_ROW_H - 40) / 2;
      ctx.font = 'bold 26px Rubik, sans-serif';
      const chipW = Math.min(ctx.measureText(status.text).width + 64, CANVAS_W - 40);
      const chipX = (CANVAS_W - chipW) / 2;
      ctx.beginPath();
      ctx.roundRect(chipX, chipY, chipW, 40, 20);
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fill();
      ctx.strokeStyle = tone;
      ctx.lineWidth = 2;
      ctx.stroke();
      // Dot then text, so tone reads even if the text is squeezed
      ctx.beginPath();
      ctx.arc(chipX + 22, chipY + 20, 7, 0, Math.PI * 2);
      ctx.fillStyle = tone;
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText(status.text, chipX + chipW / 2 + 10, chipY + 21, chipW - 56);
    }

    // Club selector row: ◀ [name] ▶
    const prev = this.#regions[0];
    const next = this.#regions[1];
    drawRegion(prev, '◀', true);
    drawRegion(next, '▶', true);
    ctx.font = 'bold 44px Rubik, sans-serif';
    ctx.fillStyle = colors.primary;
    ctx.fillText(this.#clubLabel(), CANVAS_W / 2, PAD + this.#chipH + CLUB_ROW_H / 2, CANVAS_W * 0.44);

    // Button grid
    for (const button of this.#buttons) {
      const region = this.#regions.find(r => r.id === button.id)!;
      drawRegion(region, button.label());
    }

    this.#texture.needsUpdate = true;
  }
}
